import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SchoolSummary,
  assignResponsibleToSchools,
  createSchool,
  listSchoolsSummary,
  toggleSchoolStatus,
  updateSchool,
} from '../api/schools';
import { UserRow, listUsers } from '../api/users';
import { EntityAutocomplete, EntityOption } from '../components/EntityAutocomplete';
import { useAuth } from '../auth/AuthContext';
import { downloadRecordsCsv } from '../utils/csvExport';
import { deleteDocsInCollection } from '../api/batchDelete';
import { schoolStatusLabel } from '../utils/statusLabels';
import { normalizeText } from '../utils/text';

const RESPONSIBLE_ROLES = ['responsible', 'cap', 'ae', 'cgs'];

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'default';
    default:
      return 'default';
  }
};

export const SchoolsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { role, userId } = useAuth();
  // cap/ae : accès en lecture seule, limité à leurs propres écoles.
  const isScopedRole = role === 'cap' || role === 'ae';
  const canEdit = role === 'admin';
  const canBulkDeleteSchools = role === 'admin';
  const { data, isLoading } = useQuery<SchoolSummary[]>({
    queryKey: ['schools'],
    queryFn: listSchoolsSummary,
  });
  const { data: users } = useQuery<UserRow[]>({
    queryKey: ['users'],
    queryFn: listUsers,
  });

  const userLabel = React.useCallback(
    (u: UserRow) =>
      `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
    []
  );

  const usersById = React.useMemo(() => {
    const map = new Map<string, UserRow>();
    (users ?? []).forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  const responsibleNames = React.useCallback(
    (ids?: string[]): string[] =>
      (ids ?? []).map((id) => {
        const u = usersById.get(id);
        return u ? userLabel(u) : id;
      }),
    [usersById, userLabel]
  );

  const responsibleOptions = React.useMemo<EntityOption[]>(() => {
    if (!users) return [];
    return users
      .filter((u) => u.role && RESPONSIBLE_ROLES.includes(u.role))
      .map((u) => ({
        id: u.id,
        label: userLabel(u),
        description: [u.role, u.academy, u.region, u.commune]
          .filter(Boolean)
          .join(' · '),
      }));
  }, [users, userLabel]);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'name' | 'academy' | 'cap' | 'region' | 'commune' | 'address' | 'status' | 'createdAt' | 'updatedAt'
  >('name');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [assignDialogOpen, setAssignDialogOpen] = React.useState(false);
  const [assignResponsibleId, setAssignResponsibleId] = React.useState('');
  const [assigning, setAssigning] = React.useState(false);
  const [assignError, setAssignError] = React.useState<string | null>(null);

  const [editSchool, setEditSchool] = React.useState<SchoolSummary | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editAcademy, setEditAcademy] = React.useState('');
  const [editCap, setEditCap] = React.useState('');
  const [editRegion, setEditRegion] = React.useState('');
  const [editCommune, setEditCommune] = React.useState('');
  const [editAddress, setEditAddress] = React.useState('');
  const [editStatus, setEditStatus] = React.useState<string>('active');
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createName, setCreateName] = React.useState('');
  const [createAcademy, setCreateAcademy] = React.useState('');
  const [createCap, setCreateCap] = React.useState('');
  const [createRegion, setCreateRegion] = React.useState('');
  const [createCommune, setCreateCommune] = React.useState('');
  const [createAddress, setCreateAddress] = React.useState('');
  const [createStatus, setCreateStatus] = React.useState('active');
  const [createSaving, setCreateSaving] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const handleRequestSort = (
    property:
      | 'name'
      | 'academy'
      | 'cap'
      | 'region'
      | 'commune'
      | 'address'
      | 'status'
      | 'createdAt'
      | 'updatedAt'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((s) => {
      // cap/ae ne voient que les écoles dont ils sont responsables.
      if (isScopedRole) {
        if (!userId || !(s.responsibleIds ?? []).includes(userId)) {
          return false;
        }
      }
      const haystack = normalizeText(`${s.name ?? ''} ${s.academy ?? ''} ${s.cap ?? ''} ${
        s.region ?? ''
      } ${s.commune ?? ''} ${s.address ?? ''}`);
      const matchesSearch = haystack.includes(normalizeText(search));
      const matchesStatus =
        statusFilter === 'all' || (s.status ?? 'active') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter, isScopedRole, userId]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | undefined;
      let bv: string | Date | undefined;

      switch (orderBy) {
        case 'name':
          av = (a.name ?? '').toLowerCase();
          bv = (b.name ?? '').toLowerCase();
          break;
        case 'academy':
          av = (a.academy ?? '').toLowerCase();
          bv = (b.academy ?? '').toLowerCase();
          break;
        case 'cap':
          av = (a.cap ?? '').toLowerCase();
          bv = (b.cap ?? '').toLowerCase();
          break;
        case 'region':
          av = (a.region ?? '').toLowerCase();
          bv = (b.region ?? '').toLowerCase();
          break;
        case 'commune':
          av = (a.commune ?? '').toLowerCase();
          bv = (b.commune ?? '').toLowerCase();
          break;
        case 'address':
          av = (a.address ?? '').toLowerCase();
          bv = (b.address ?? '').toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toLowerCase();
          bv = (b.status ?? '').toLowerCase();
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const allSelected =
    canEdit &&
    filtered.length > 0 &&
    filtered.every((s) => selectedIds.includes(s.id));
  const someSelected =
    canEdit &&
    filtered.length > 0 &&
    filtered.some((s) => selectedIds.includes(s.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((s) => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleConfirmDeleteSelected = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDocsInCollection('schools', selectedIds);
      await queryClient.invalidateQueries({ queryKey: ['schools'] });
      setSelectedIds([]);
      setDeleteDialogOpen(false);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "La suppression a échoué.");
    } finally {
      setDeleting(false);
    }
  };

  const openAssignDialog = () => {
    setAssignResponsibleId('');
    setAssignError(null);
    setAssignDialogOpen(true);
  };

  const closeAssignDialog = () => {
    if (assigning) return;
    setAssignDialogOpen(false);
    setAssignError(null);
  };

  const handleConfirmAssign = async () => {
    if (!assignResponsibleId) {
      setAssignError('Veuillez sélectionner un responsable.');
      return;
    }
    if (!selectedIds.length) return;
    setAssigning(true);
    setAssignError(null);
    try {
      await assignResponsibleToSchools(assignResponsibleId, selectedIds);
      await queryClient.invalidateQueries({ queryKey: ['schools'] });
      setSelectedIds([]);
      setAssignDialogOpen(false);
    } catch (e) {
      setAssignError(
        e instanceof Error ? e.message : "L'assignation a échoué."
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleToggleStatus = async (school: SchoolSummary) => {
    await toggleSchoolStatus(school.id, school.status);
    await queryClient.invalidateQueries({ queryKey: ['schools'] });
  };

  const openEditSchool = (s: SchoolSummary) => {
    setEditSchool(s);
    setEditName(s.name ?? '');
    setEditAcademy(s.academy ?? '');
    setEditCap(s.cap ?? '');
    setEditRegion(s.region ?? '');
    setEditCommune(s.commune ?? '');
    setEditAddress(s.address ?? '');
    setEditStatus(s.status ?? 'active');
    setEditError(null);
  };

  const closeEditSchool = () => {
    if (editSaving) return;
    setEditSchool(null);
    setEditError(null);
  };

  const openCreateSchool = () => {
    setEditSchool(null);
    setEditError(null);
    setCreateName('');
    setCreateAcademy('');
    setCreateCap('');
    setCreateRegion('');
    setCreateCommune('');
    setCreateAddress('');
    setCreateStatus('active');
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreateSchool = () => {
    if (createSaving) return;
    setCreateOpen(false);
    setCreateError(null);
  };

  const handleSaveCreateSchool = async () => {
    if (!createName.trim()) {
      setCreateError('Le nom est obligatoire.');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      await createSchool({
        name: createName.trim(),
        academy: createAcademy,
        cap: createCap,
        region: createRegion,
        commune: createCommune,
        address: createAddress,
        status: createStatus,
      });
      await queryClient.invalidateQueries({ queryKey: ['schools'] });
      setCreateOpen(false);
      setPage(0);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSaveSchool = async () => {
    if (!editSchool) return;
    if (!editName.trim()) {
      setEditError('Le nom est obligatoire.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updateSchool(editSchool.id, {
        name: editName.trim(),
        academy: editAcademy,
        cap: editCap,
        region: editRegion,
        commune: editCommune,
        address: editAddress,
        status: editStatus,
      });
      await queryClient.invalidateQueries({ queryKey: ['schools'] });
      setEditSchool(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleExportCsv = () => {
    const rows: Array<Record<string, unknown>> = sorted.map((s) => ({
      schoolId: s.id,
      name: s.name ?? '',
      academy: s.academy ?? '',
      cap: s.cap ?? '',
      region: s.region ?? '',
      commune: s.commune ?? '',
      address: s.address ?? '',
      responsibles: responsibleNames(s.responsibleIds).join(' | '),
      responsibleIds: (s.responsibleIds ?? []).join(' | '),
      status: s.status ?? '',
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
    }));
    downloadRecordsCsv('ecoles', rows);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Écoles
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Nom, academy, région, commune, adresse..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 'auto' }, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openCreateSchool}
            >
              Ajouter
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            disabled={!sorted.length}
            onClick={handleExportCsv}
          >
            Exporter CSV
          </Button>
        </Box>
      </Box>
      {canEdit && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedIds.length
              ? `${selectedIds.length} sélectionnée(s)`
              : 'Aucune sélection'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<GroupAddIcon />}
              disabled={!selectedIds.length}
              onClick={openAssignDialog}
            >
              Assigner un responsable
            </Button>
            {canBulkDeleteSchools && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={!selectedIds.length}
                onClick={() => {
                  setDeleteError(null);
                  setDeleteDialogOpen(true);
                }}
              >
                Supprimer la sélection
              </Button>
            )}
          </Box>
        </Box>
      )}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canEdit && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Nom
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'academy' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'academy'}
                  direction={orderBy === 'academy' ? order : 'asc'}
                  onClick={() => handleRequestSort('academy')}
                >
                  Academy
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'cap' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'cap'}
                  direction={orderBy === 'cap' ? order : 'asc'}
                  onClick={() => handleRequestSort('cap')}
                >
                  CAP
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'region' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'region'}
                  direction={orderBy === 'region' ? order : 'asc'}
                  onClick={() => handleRequestSort('region')}
                >
                  Région
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'commune' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'commune'}
                  direction={orderBy === 'commune' ? order : 'asc'}
                  onClick={() => handleRequestSort('commune')}
                >
                  Commune
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'address' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'address'}
                  direction={orderBy === 'address' ? order : 'asc'}
                  onClick={() => handleRequestSort('address')}
                >
                  Adresse
                </TableSortLabel>
              </TableCell>
              <TableCell>Responsables</TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créée le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mise à jour le
                </TableSortLabel>
              </TableCell>
              {canEdit && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={canEdit ? 12 : 10}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((s) => (
                <TableRow key={s.id} hover selected={canEdit && selectedIds.includes(s.id)}>
                  {canEdit && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(s.id)}
                        onChange={() => toggleOne(s.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{s.name ?? '—'}</TableCell>
                  <TableCell>{s.academy ?? '—'}</TableCell>
                  <TableCell>{s.cap ?? '—'}</TableCell>
                  <TableCell>{s.region ?? '—'}</TableCell>
                  <TableCell>{s.commune ?? '—'}</TableCell>
                  <TableCell>{s.address ?? '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    {(() => {
                      const names = responsibleNames(s.responsibleIds);
                      if (!names.length) {
                        return (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        );
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {names.map((name, i) => (
                            <Chip
                              key={`${s.id}-resp-${i}`}
                              label={name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={schoolStatusLabel(s.status)}
                      color={statusColor(s.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(s.createdAt)}</TableCell>
                  <TableCell>{formatDate(s.updatedAt)}</TableCell>
                  {canEdit && (
                    <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flexWrap: 'nowrap',
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="Modifier l'école">
                          <IconButton size="small" onClick={() => openEditSchool(s)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            s.status === 'active'
                              ? 'Désactiver cette école'
                              : 'Activer cette école'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(s)}
                          >
                            {s.status === 'active' ? (
                              <BlockIcon fontSize="small" />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={canEdit ? 12 : 10}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune école trouvée.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100, 500]}
      />
      <Dialog open={createOpen} onClose={closeCreateSchool} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvelle école</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {createError && (
            <Alert severity="error" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          <TextField
            label="Nom"
            required
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            disabled={createSaving}
          />
          <TextField
            label="Académie"
            value={createAcademy}
            onChange={(e) => setCreateAcademy(e.target.value)}
            disabled={createSaving}
          />
          <TextField
            label="CAP"
            value={createCap}
            onChange={(e) => setCreateCap(e.target.value)}
            disabled={createSaving}
          />
          <TextField
            label="Région"
            value={createRegion}
            onChange={(e) => setCreateRegion(e.target.value)}
            disabled={createSaving}
          />
          <TextField
            label="Commune"
            value={createCommune}
            onChange={(e) => setCreateCommune(e.target.value)}
            disabled={createSaving}
          />
          <TextField
            label="Adresse"
            value={createAddress}
            onChange={(e) => setCreateAddress(e.target.value)}
            disabled={createSaving}
            multiline
            minRows={2}
          />
          <FormControl size="small">
            <InputLabel id="create-school-status-label">Statut</InputLabel>
            <Select
              labelId="create-school-status-label"
              label="Statut"
              value={createStatus}
              onChange={(e) => setCreateStatus(e.target.value)}
              disabled={createSaving}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateSchool} color="inherit" disabled={createSaving}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleSaveCreateSchool()}
            variant="contained"
            disabled={createSaving}
          >
            {createSaving ? 'Création…' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editSchool)} onClose={closeEditSchool} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier l&apos;école</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {editError && (
            <Alert severity="error" onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}
          <TextField
            label="Nom"
            required
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={editSaving}
          />
          <TextField
            label="Académie"
            value={editAcademy}
            onChange={(e) => setEditAcademy(e.target.value)}
            disabled={editSaving}
          />
          <TextField
            label="CAP"
            value={editCap}
            onChange={(e) => setEditCap(e.target.value)}
            disabled={editSaving}
          />
          <TextField
            label="Région"
            value={editRegion}
            onChange={(e) => setEditRegion(e.target.value)}
            disabled={editSaving}
          />
          <TextField
            label="Commune"
            value={editCommune}
            onChange={(e) => setEditCommune(e.target.value)}
            disabled={editSaving}
          />
          <TextField
            label="Adresse"
            value={editAddress}
            onChange={(e) => setEditAddress(e.target.value)}
            disabled={editSaving}
            multiline
            minRows={2}
          />
          <FormControl size="small">
            <InputLabel id="edit-school-status-label">Statut</InputLabel>
            <Select
              labelId="edit-school-status-label"
              label="Statut"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value)}
              disabled={editSaving}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditSchool} color="inherit" disabled={editSaving}>
            Annuler
          </Button>
          <Button onClick={() => void handleSaveSchool()} variant="contained" disabled={editSaving}>
            {editSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialogOpen} onClose={closeAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assigner un responsable</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {assignError && (
            <Alert severity="error" onClose={() => setAssignError(null)}>
              {assignError}
            </Alert>
          )}
          <DialogContentText>
            Le responsable sélectionné sera ajouté à {selectedIds.length} école(s).
            Les responsables déjà assignés sont conservés.
          </DialogContentText>
          <EntityAutocomplete
            label="Responsable"
            options={responsibleOptions}
            valueId={assignResponsibleId}
            onChangeId={setAssignResponsibleId}
            disabled={assigning}
            required
            helperText="Rôles éligibles : responsable, CAP, AE, CGS."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog} color="inherit" disabled={assigning}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleConfirmAssign()}
            variant="contained"
            disabled={assigning || !assignResponsibleId}
          >
            {assigning ? 'Assignation…' : 'Assigner'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer les écoles sélectionnées</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedIds.length} école(s) seront définitivement supprimées (admin uniquement). Les
            sous-collections éventuelles ne sont pas gérées ici.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button onClick={handleConfirmDeleteSelected} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Suppression…' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

